// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useParams } from 'react-router-dom';
import { useGetRidehailDetailsQuery } from '../queries/getRidehailDetails.query.generated';

import { Container, Alert, LinearProgress } from '@mui/material';

import Toolbar from '@components/Utils/Toolbar';
import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import { RidehailDetailsView } from '@hopdrive/feature-ridehails';
import { DealerRidehailsHost } from '../host';

// COMPONENT ---------------------------------------------------------------- //

/**
 * The details VIEW (layout, map, polling, actions) is the shared feature package's
 * RidehailDetailsView — the same component the embed portal renders. This page keeps
 * only what is dealer-specific: the generated details query, the loading/error/
 * not-found chrome, and the app toolbar shell around the feature-owned toolbar
 * content. The map's <APIProvider> is mounted app-wide in App.tsx.
 */
const RidehailDetailsPage: React.FC = () => {
  const { moveId } = useParams<{ moveId: string }>();

  const { data, loading, error, refetch } = useGetRidehailDetailsQuery({
    variables: { move_id: parseInt(moveId || '0') },
    skip: !moveId,
  });

  const move = data?.moves_by_pk;

  if (loading) return <LinearProgress />;

  if (error) {
    // The app fallback takes no title/refetch props; fold the title into the message.
    return <DefaultFallback severity='error' message={`Error loading rideshare details: ${error.message}`} />;
  }

  if (!move) {
    return (
      <Container maxWidth='lg' sx={{ pt: 4, pb: 4 }}>
        <Alert severity='error'>Move not found</Alert>
      </Container>
    );
  }

  return (
    <RidehailDetailsView
      move={move}
      onRefetch={refetch}
      renderToolbar={content => (
        <Toolbar
          back
          title='Rideshare Details'
          refetch={async () => {
            await refetch();
          }}>
          {content}
        </Toolbar>
      )}
    />
  );
};

// EXPORT ---------------------------------------------------------------- //

// The shared view reads the app host (auth, rides URL, routing) from context —
// provide it at the page boundary.
const RidehailDetailsPageWithHost: React.FC = () => (
  <DealerRidehailsHost>
    <RidehailDetailsPage />
  </DealerRidehailsHost>
);

export default RidehailDetailsPageWithHost;
